/**
 * Quiz: "Which Chat Is the Fattest?"
 * Picks the fattest chat + 3 distractors. User guesses which is fattest.
 */

import { esc } from './shared.js';

export function renderQuiz(result) {
  const choices = pickQuizChoices(result);
  if (!choices) return '';

  const { correct, distractors } = choices;
  const shuffled = shuffle([correct, ...distractors]);

  const cards = shuffled.map((conv, i) => {
    const isCorrect = conv === correct;
    return `
      <div class="quiz-choice" data-quiz-idx="${i}" data-correct="${isCorrect}" data-bloat="${(conv.bloatScore * 100).toFixed(0)}">
        <div class="quiz-title">${esc(conv.title)}</div>
        <div class="quiz-hint">${conv.messageCount} messages</div>
      </div>
    `;
  }).join('');

  return `
    <section class="section" id="section-quiz">
      <div class="section-label">Pop Quiz</div>
      <div class="section-title">Which chat is the fattest?</div>
      <div class="section-desc">
        Pick the one you think is using the most context. Only titles and message counts visible.
      </div>

      <div class="quiz-grid" id="quiz-grid">
        ${cards}
      </div>

      <div class="quiz-result" id="quiz-result"></div>
    </section>
  `;
}

/**
 * Pick the fattest named conversation + 3 distractors from lower-bloat chats.
 * Returns null if < 4 named conversations available.
 */
export function pickQuizChoices(result) {
  const named = result.conversations
    .filter(c => c.title && c.title !== 'New chat' && c.title.trim().length > 0)
    .sort((a, b) => b.bloatScore - a.bloatScore);

  if (named.length < 4) return null;

  const correct = named[0];

  // Pick 3 distractors from the lower half (variety, not just the leanest)
  const pool = named.slice(Math.floor(named.length * 0.3));
  const distractors = [];
  const used = new Set([0]);

  while (distractors.length < 3 && pool.length > 0) {
    const idx = Math.floor(Math.random() * pool.length);
    const pick = pool.splice(idx, 1)[0];
    if (!used.has(pick)) {
      distractors.push(pick);
      used.add(pick);
    }
  }

  if (distractors.length < 3) {
    // Fall back to next in line
    for (let i = 1; i < named.length && distractors.length < 3; i++) {
      if (!used.has(named[i])) {
        distractors.push(named[i]);
        used.add(named[i]);
      }
    }
  }

  if (distractors.length < 3) return null;

  return { correct, distractors };
}

/**
 * Wire up quiz click handlers via event delegation on the quiz container.
 */
export function initQuiz() {
  const grid = document.getElementById('quiz-grid');
  const resultEl = document.getElementById('quiz-result');
  if (!grid || !resultEl) return;

  let answered = false;

  grid.addEventListener('click', (e) => {
    if (answered) return;

    const choice = e.target.closest('.quiz-choice');
    if (!choice) return;

    answered = true;
    const isCorrect = choice.dataset.correct === 'true';
    const bloat = choice.dataset.bloat;

    // Highlight all choices
    grid.querySelectorAll('.quiz-choice').forEach(el => {
      if (el.dataset.correct === 'true') {
        el.classList.add('correct');
      } else if (el === choice && !isCorrect) {
        el.classList.add('wrong');
      } else {
        el.classList.add('dimmed');
      }
    });

    // Show result message
    if (isCorrect) {
      resultEl.className = 'quiz-result show correct-result';
      resultEl.innerHTML = `You know your mess &mdash; that's the first step. <strong>${bloat}% bloat.</strong>`;
    } else {
      const correctEl = grid.querySelector('[data-correct="true"]');
      const correctTitle = correctEl.querySelector('.quiz-title').textContent;
      const correctBloat = correctEl.dataset.bloat;
      resultEl.className = 'quiz-result show wrong-result';
      resultEl.innerHTML = `Nope! <strong>${esc(correctTitle)}</strong> is at <strong>${correctBloat}% bloat</strong>.`;
    }
  });
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
